import { useState, useCallback } from "react"
import { initiateIntegrationAuthApi } from "@/api/integrations"
import { API_BASE_URL } from "@/constants/api.constants"

/**
 * Get OAuth provider for integration ID.
 * Exported for testability — the integration→provider routing is a
 * load-bearing decision the test suite needs to pin.
 */
export function getProviderForIntegration(integrationId: string): string {
  if (integrationId.includes("gmail") || integrationId.includes("google")) {
    return "google"
  }
  if (integrationId.includes("outlook")) {
    return "microsoft"
  }
  return "google"
}

/**
 * Hook to handle OAuth flow for connecting integrations
 */
export function useConnectIntegration() {
  const [connectingId, setConnectingId] = useState<string | null>(null)

  const connect = useCallback(async (integrationId: string): Promise<boolean> => {
    try {
      setConnectingId(integrationId)
      const provider = getProviderForIntegration(integrationId)

      // Open the popup IMMEDIATELY with about:blank — pre-await — so
      // the browser still sees window.open() as a direct response to
      // the user click. If we wait until after `await
      // initiateIntegrationAuthApi(...)`, Safari + Chrome (in stricter
      // popup-blocker modes) drop the user-gesture context and refuse
      // to open the window. Symptom: button stays in "Connecting…"
      // forever because popup is null and the listener never fires.
      // We update the popup's location once the authUrl resolves.
      const width = 500
      const height = 600
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(
        "about:blank",
        "oauth-nylas",
        `width=${width},height=${height},left=${left},top=${top}`
      )

      if (!popup) {
        console.warn("[useConnectIntegration] popup blocked at open time")
        setConnectingId(null)
        return false
      }

      let result
      try {
        result = await initiateIntegrationAuthApi({ provider })
      } catch (err) {
        popup.close()
        throw err
      }

      // Same-origin write to popup.location — preserves the popup
      // window we already opened in the user-gesture frame.
      popup.location.href = result.authUrl

      // Return promise that resolves when OAuth completes
      return new Promise<boolean>((resolve) => {
        // Origin check uses the SAME base URL the API client itself uses
        // (derived from environments.json + the active backend env in
        // localStorage), not import.meta.env.VITE_API_BASE_URL. Older code
        // read the Vite env var directly, which was never set on the
        // staging Vercel branch — `new URL('')` threw inside the listener
        // and the nylas-oauth-success message was eaten silently.
        // API_BASE_URL is guaranteed populated because the same constant
        // successfully made the /oauth/nylas/start call we're awaiting
        // above.
        let expectedOrigin: string
        try {
          expectedOrigin = new URL(API_BASE_URL).origin
        } catch (e) {
          console.error("[useConnectIntegration] could not parse API_BASE_URL", API_BASE_URL, e)
          expectedOrigin = ""
        }

        const handleMessage = (event: MessageEvent) => {
          if (expectedOrigin && event.origin !== expectedOrigin) {
            return; // Ignore messages from unexpected origins
          }

          // Backend (oauth.ts:522 / 445) posts message types prefixed
          // with `nylas-` (`nylas-oauth-success`, `nylas-oauth-error`).
          // The previous matcher looked for the un-prefixed `oauth-*`
          // forms and never fired — that's why the button hung even
          // after a successful Google consent. Match both prefixes for
          // defense-in-depth in case other callbacks (Google direct,
          // Microsoft) start emitting the un-prefixed variant.
          const t = event.data?.type
          const isSuccess = t === "nylas-oauth-success" || t === "oauth-success"
          const isError = t === "nylas-oauth-error" || t === "oauth-error"
          if (isSuccess) {
            cleanup()
            popup.close()
            resolve(true)
          } else if (isError) {
            cleanup()
            popup.close()
            console.error("OAuth error:", event.data?.error)
            resolve(false)
          }
        }

        const cleanup = () => {
          window.removeEventListener("message", handleMessage)
          clearInterval(checkPopup)
          setConnectingId(null)
        }

        window.addEventListener("message", handleMessage)

        // Check if popup is closed manually
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            cleanup()
            resolve(false)
          }
        }, 500)
      })
    } catch (error) {
      console.error("Failed to initiate connection:", error)
      setConnectingId(null)
      return false
    }
  }, [])

  return {
    connect,
    connectingId,
    isConnecting: connectingId !== null,
  }
}